import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Volume2 } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import LessonCard from "@/components/LessonCard";
import WordSoundCard from "@/components/WordSoundCard";
import AZSounds from "@/components/AZSounds";
import PhonicGuide from "@/components/PhonicGuide";
import ABCSong from "@/components/ABCSong";
import { useLessons } from "@/hooks/useLessons";
import { WORD_SOUND_CARDS } from "@/lib/wordSoundData";
import { toast } from "sonner";

export default function TrackPage() {
  const { trackId } = useParams();
  const nav = useNavigate();
  const validTrack = trackId === "basic" || trackId === "advanced";
  const { data, loading, error } = useLessons(validTrack ? trackId : null);

  useEffect(() => {
    if (!validTrack) nav("/");
  }, [validTrack, nav]);

  useEffect(() => {
    if (error) toast.error("Failed to load lessons");
  }, [error]);

  const variant = trackId === "advanced" ? "advanced" : "basic";
  const accent =
    variant === "advanced"
      ? { chip: "bg-amber-50 text-amber-700", ring: "text-amber-500" }
      : { chip: "bg-sky-50 text-sky-700", ring: "text-sky-500" };

  return (
    <div className="min-h-screen relative">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <Link
          to="/"
          data-testid="track-back-home"
          className="inline-flex items-center gap-2 text-sm font-bold text-slate-600 hover:text-slate-900 transition-colors mb-8"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        {loading && (
          <div className="flex items-center justify-center py-32 text-slate-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading lessons…
          </div>
        )}

        {!loading && data && (
          <>
            <div className={`inline-block ${accent.chip} px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest mb-3`}>
              {variant === "advanced" ? "Ages 16 – 25" : "Ages 5 – 16"}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900">
              {data.title}
            </h1>
            <p className="mt-4 text-slate-600 text-lg max-w-2xl">
              Tap <span className={`font-bold ${accent.ring}`}>Sound</span> to hear the
              letter or pattern, and <span className={`font-bold ${accent.ring}`}>Word</span> to
              hear the full example.
            </p>

            <div className="mt-10 grid gap-5 sm:gap-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
              {data.lessons.map((lesson) => (
                <LessonCard key={lesson.id} lesson={lesson} variant={variant} />
              ))}
            </div>

            {variant === "basic" && (
              <section
                id="word-sound-cards"
                data-testid="wordsound-section"
                className="mt-20"
              >
                <div className="text-xs font-bold uppercase tracking-widest text-sky-600 mb-2 inline-flex items-center gap-1.5">
                  <Volume2 className="w-3.5 h-3.5" /> Whole-word cards
                </div>
                <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                  Now let&rsquo;s read the whole word
                </h2>
                <p className="mt-3 text-slate-600 text-lg max-w-2xl">
                  Tap any card to hear the whole word. Great for building
                  sight-reading of short, common words.
                </p>

                <div className="mt-8 grid gap-5 sm:gap-6 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4">
                  {WORD_SOUND_CARDS.map((item) => (
                    <WordSoundCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            )}

            <AZSounds accent={variant === "advanced" ? "amber" : "sky"} />

            {variant === "basic" && <ABCSong />}

            {variant === "advanced" && <PhonicGuide />}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
